import axiosInstance from 'utils/axios';

import type { SurveyConfig } from './types';

export type ApiSurveyPayload = {
  title: string;
  subtitle: string;
  q1: string;
  q2: string;
  q3: string;
  thanks_title: string;
  thanks_body: string;
};

export type CustomerSurveySource = 'dashboard' | 'request';

export type CustomerSurveyTriggerType = 'after_onboarding' | 'completed_request';

export type CustomerSurveyEligibilityParams = {
  source: CustomerSurveySource;
  triggerType?: CustomerSurveyTriggerType;
  requestId?: string | number;
};

export type CustomerSurveyEligibilityResponse = {
  success: boolean;
  eligible: boolean;
  reason?: string;
  source?: CustomerSurveySource;
  trigger_type?: CustomerSurveyTriggerType | null;
  survey?: ApiSurveyPayload;
  prompt_id?: number;
  expires_at?: string;
};

export type CustomerSurveySubmitBody = {
  recommend: number;
  satisfaction: number;
  comment?: string;
  source: CustomerSurveySource;
  source_request_id?: string | number;
};

export type CustomerSurveySubmitResponse = {
  success: boolean;
  thanks_title?: string;
  thanks_body?: string;
  error?: string;
};

export class CustomerSurveyApiError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'CustomerSurveyApiError';
    this.status = status;
  }
}

// Dedupe concurrent eligibility checks the same way the fe-enterprise
// implementation does, keyed by a token identity (here: whatever
// identifies the current session — see CustomerSurveyLoginPrompt).
const eligibilityInflight = new Map<string, Promise<CustomerSurveyEligibilityResponse>>();

export function surveyFromApi(raw: ApiSurveyPayload): SurveyConfig {
  return {
    title: raw.title,
    subtitle: raw.subtitle,
    q1: raw.q1,
    q2: raw.q2,
    q3: raw.q3,
    thanksTitle: raw.thanks_title,
    thanksBody: raw.thanks_body,
  };
}

function eligibilityQuery(params: CustomerSurveyEligibilityParams): string {
  const query = new URLSearchParams({ source: params.source });
  if (params.triggerType) query.set('trigger_type', params.triggerType);
  if (params.requestId !== undefined && params.requestId !== null) {
    query.set('request_id', String(params.requestId));
  }
  return query.toString();
}

export async function fetchCustomerSurveyEligibility(
  sessionKey: string,
  params: CustomerSurveyEligibilityParams = { source: 'dashboard' },
): Promise<CustomerSurveyEligibilityResponse> {
  const query = eligibilityQuery(params);
  const inflightKey = `${sessionKey}|${query}`;
  const existing = eligibilityInflight.get(inflightKey);
  if (existing) return existing;

  const request = (async () => {
    try {
      const res = await axiosInstance.get<CustomerSurveyEligibilityResponse>(
        `/api/v2/customer_survey/eligibility?${query}`,
      );

      if (res.data?.success === false) {
        throw new CustomerSurveyApiError(res.data.reason ?? 'Unable to check survey eligibility', res.status);
      }

      return res.data;
    } catch (err: any) {
      if (err instanceof CustomerSurveyApiError) throw err;
      const status = err?.response?.status;
      throw new CustomerSurveyApiError(
        err?.response?.data?.reason ?? 'Unable to check survey eligibility',
        status,
      );
    }
  })().finally(() => {
    eligibilityInflight.delete(inflightKey);
  });

  eligibilityInflight.set(inflightKey, request);
  return request;
}

export async function submitCustomerSurveyResponse(
  body: CustomerSurveySubmitBody,
): Promise<CustomerSurveySubmitResponse> {
  try {
    const res = await axiosInstance.post<CustomerSurveySubmitResponse>(
      '/api/v2/customer_survey/responses',
      body,
    );

    if (res.data?.success === false) {
      throw new CustomerSurveyApiError(res.data.error ?? 'Unable to submit feedback', res.status);
    }

    return res.data;
  } catch (err: any) {
    if (err instanceof CustomerSurveyApiError) throw err;
    const status = err?.response?.status;
    throw new CustomerSurveyApiError(err?.response?.data?.error ?? 'Unable to submit feedback', status);
  }
}
